// Palworld TCG keyword cluster for the market features shipped this cycle:
// card trading, the public want list, live raw/graded auction data, graded
// comps, per-card liquidity scoring and the TCGplayer price feed.
//
// Each topic owns one query family that no existing guide targets, and expands
// into exactly five AEO-shaped articles (overview, step-by-step, reference,
// mistakes, FAQ) so every title stays unique and every answer stays
// extractable. Structure mirrors src/lib/guides-tcg-longtail.ts.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-card-trading",
    title: "Palworld TCG Card Trading",
    desc: "How to trade Palworld TCG cards safely: value both sides by printed card code, read the market spread, and use the Pal Stars trade board instead of guessing.",
    keywords:
      "palworld tcg card trading, palworld card trade value, trade palworld tcg cards online, palworld card trading board, how to trade palworld cards fairly",
    hub: "/trades",
    hubLabel: "Palworld TCG trade board",
    tag: "TCG Trading",
    question: "How do you trade Palworld TCG cards without losing value?",
    answer:
      "Price both sides of a Palworld trade by exact printed card code first, then trade card-for-card within roughly 10% of the two market values. Codes such as EBP01-001 and ETD01-012TSP price differently even when the artwork looks identical, so a code-level quote is the only fair basis for a swap.",
    steps: [
      ["Write down the exact printed code on both cards", "The code sits on the card face — EBP01-001, ETD02-023, ESOUL-002. Parallels carry a suffix such as TSP or TSR and are separate market items."],
      ["Pull a market quote for each code", "Open the price hub for both codes and read the market value, not the highest asking price. Asking prices are aspirations; sold prices are the trade."],
      ["Compare condition honestly", "A creased or surface-scratched copy trades below a clean one even ungraded. Photograph corners and edges before you agree."],
      ["Keep the swap inside a 10% value band", "If one side is more than about 10% heavier, add a second lower-value card rather than accepting the gap."],
      ["Post the swap on the trade board", "List what you are giving and what you want, so the other side sees the codes and the quote instead of a photo and a promise."],
    ],
    data: [
      ["Trade unit", "One printed card code, not one Pal — a Pal can appear across several codes and rarities."],
      ["Fair band", "Within about 10% of combined market value on both sides."],
      ["Parallel handling", "TSP and TSR parallels trade as their own item, usually above the base printing."],
      ["Condition", "Ungraded condition still moves value; heavy edge wear can halve a mid-rarity card."],
      ["Soul cards", "Soul printings such as ESOUL-002 are traded far less often, so quotes move on very thin evidence."],
    ],
    pitfalls: [
      ["Trading by Pal name", "\"A Cattiva for a Cattiva\" can be a 20x value gap between a base common and a gold Soul parallel. Trade by code."],
      ["Anchoring on the highest listing", "One optimistic listing is not a market. Use the market value and the sold evidence behind it."],
      ["Ignoring the spread", "A wide gap between asking and sold price means the card is illiquid — build in more margin before you swap it."],
      ["Sending first without a record", "Keep the posted trade and the agreed codes in writing so both sides can point at the same terms."],
    ],
    faq: [
      ["Is trading Palworld TCG cards worth it versus selling?", "Trading avoids marketplace fees entirely, so a fair code-for-code swap usually beats selling one card and buying another."],
      ["How do I know a Palworld card's trade value?", "Read its market value on the price hub for that exact printed code, then check the sold evidence and the spread behind it."],
      ["Can I trade parallels for base cards?", "Yes, but parallels normally carry a premium, so expect to receive more than one base card in return."],
      ["Do graded cards trade differently?", "Yes. A graded copy trades against graded comps for the same grade, not against raw prices."],
    ],
  },
  {
    slug: "palworld-tcg-want-list",
    title: "Palworld TCG Want List",
    desc: "Use a Palworld TCG want list to find missing set cards fast: how to post the codes you need, prioritise by liquidity, and match with trade partners.",
    keywords:
      "palworld tcg want list, palworld card wanted list, find missing palworld tcg cards, palworld set completion checklist, palworld card want board",
    hub: "/want-list",
    hubLabel: "Palworld TCG want list",
    tag: "TCG Trading",
    question: "What is a Palworld TCG want list for?",
    answer:
      "A want list is the public record of the printed card codes you still need, so trade partners can match your gaps against their spares without messaging first. Posting codes rather than Pal names is what makes a want list usable: the code fixes the set, rarity and parallel in one string.",
    steps: [
      ["List the codes you are missing, not the Pals", "Work from the set list and note each missing code, including whether you need the base printing or a parallel."],
      ["Rank by what actually finishes a set", "Commons and uncommons close a set fastest. Chase parallels are cheap to want and slow to land."],
      ["Post the list publicly", "A posted want list is crawlable and searchable, so partners find you instead of the other way round."],
      ["Check liquidity before you chase", "A high-liquidity card will appear in trades within days; a thin one may take months. Plan the order accordingly."],
      ["Prune what you have landed", "Stale wants waste partner time. Remove filled entries the same week they arrive."],
    ],
    data: [
      ["Entry format", "One printed card code per line, with the printing you need (base, TSP, TSR)."],
      ["Practical cap", "Around 40 active wants — beyond that partners stop reading."],
      ["Best first targets", "Base-rarity holes in the set you are closest to completing."],
      ["Liquidity signal", "A 0-100 score per card on the price index; higher means it trades more often."],
      ["Refresh cadence", "Weekly, so filled wants leave the list quickly."],
    ],
    pitfalls: [
      ["Wanting only chase cards", "A want list of nothing but SSP and OSR parallels almost never matches. Mix in the cheap holes."],
      ["Leaving filled wants up", "Partners stop trusting a list that sends them chasing cards you already own."],
      ["Not naming the parallel", "\"EBP01-014\" and \"EBP01-014TSP\" are different items; be explicit about which one closes your set."],
      ["Ignoring price movement", "A want you posted at one market level may cost far more now — recheck before agreeing a swap."],
    ],
    faq: [
      ["How many cards should a Palworld want list hold?", "Keep it around 40 or fewer active entries so partners can scan the whole list in one pass."],
      ["Should I list parallels separately from base cards?", "Yes — the suffix changes the market item, so list the exact printing you need."],
      ["Does a public want list help me trade faster?", "It does, because partners can search your posted codes instead of opening a conversation to find out what you need."],
      ["What should I want first to finish a set?", "Base commons and uncommons: they are the cheapest and most liquid holes in any Palworld set."],
    ],
  },
  {
    slug: "palworld-card-auction-prices",
    title: "Palworld Card Auction Prices",
    desc: "Read live Palworld TCG auction data before you bid: how raw and graded listings differ, what the ask spread means, and when an auction beats a fixed price.",
    keywords:
      "palworld card auction prices, palworld tcg ebay auctions, palworld card bidding guide, palworld tcg live listings, palworld card sold prices",
    hub: "/prices",
    hubLabel: "Palworld TCG price hub",
    tag: "TCG Market",
    question: "How do you read Palworld TCG auction prices?",
    answer:
      "Judge a Palworld auction against sold prices for the same printed code and condition, never against the other open listings. Live asks show what sellers hope for; sold prices show what buyers paid. When the live low sits under the sold market value, the auction is the better buy.",
    steps: [
      ["Confirm the auction is the exact printed code", "Titles often omit the suffix. Match the photo's code before you bid, or you may be bidding on a parallel."],
      ["Separate raw from graded listings", "Graded copies carry a grading premium that has nothing to do with the raw market. Compare like with like."],
      ["Check the sold evidence", "Look at recent sold prices for that code and condition; that is the price band the auction should close inside."],
      ["Read the ask spread", "A wide gap between the lowest and highest live ask means the card is thinly traded and the auction outcome is unpredictable."],
      ["Set a walk-away number before bidding", "Write the maximum from the sold band, then stop. Auction momentum is the most expensive part of card buying."],
    ],
    data: [
      ["Live low", "The cheapest current listing for the code — the price you can actually buy at now."],
      ["Market value", "A sold-weighted level, the fairest single number for a card."],
      ["Ask spread", "High ask minus low ask; wide means illiquid, narrow means well-priced."],
      ["Graded premium", "How much more graded copies fetch than raw for the same code."],
      ["Listing count", "How many copies are live at once; more copies means more price competition."],
    ],
    pitfalls: [
      ["Bidding against the highest listing", "A single high listing is not evidence of value; sold prices are."],
      ["Comparing a graded auction to raw prices", "The slab premium makes that comparison meaningless."],
      ["Ignoring shipping and fees", "Total landed cost is the real price. Add it before comparing to market value."],
      ["Bidding on a parallel by mistake", "Suffixes such as TSP change the item entirely. Verify from the card face."],
    ],
    faq: [
      ["Are Palworld card auctions cheaper than fixed prices?", "Often yes on liquid cards with many bidders absent, but thin cards can close above market when two collectors compete."],
      ["Why do two listings for the same Palworld card differ so much?", "Condition, printing suffix and graded status all move price, and some sellers simply price optimistically."],
      ["What does the ask spread tell me?", "A wide spread signals a thin market, so treat any single price as weak evidence."],
      ["Should I bid on raw or graded Palworld cards?", "Raw if you want playables or set completion; graded only if you value the certified grade at its premium."],
    ],
  },
  {
    slug: "palworld-graded-card-comps",
    title: "Palworld Graded Card Comps",
    desc: "Compare Palworld graded card sales properly: match grade, printing and population, and work out whether the graded premium justifies submitting a raw copy.",
    keywords:
      "palworld graded card comps, psa 10 palworld card price, palworld tcg graded premium, palworld card population report, is grading palworld cards worth it",
    hub: "/prices",
    hubLabel: "Palworld TCG price hub",
    tag: "TCG Market",
    question: "How do you compare Palworld graded card prices?",
    answer:
      "A graded Palworld comp is only valid when the grade, the printed code and the grading company all match. A PSA 10 of EBP01-001 tells you nothing about a PSA 9 of the same card, and nothing at all about a parallel printing of it. Compare within the exact triple, then check how many copies exist at that grade.",
    steps: [
      ["Fix the printed code first", "Graded titles frequently drop the suffix. Read the code off the slab label."],
      ["Match the grading company and grade", "Different companies grade to different standards, so their tens are not interchangeable comps."],
      ["Check population at that grade", "A low population at the top grade supports a premium; a large population caps it."],
      ["Compute the graded premium", "Divide the graded sold price by the raw market value for the same code. Under about 3x rarely pays for submission on mid-rarity cards."],
      ["Decide raw sale versus submission", "Add grading fees, shipping and turnaround risk before assuming a submission is profit."],
    ],
    data: [
      ["Valid comp", "Same printed code, same company, same numeric grade."],
      ["Population", "How many copies are certified at that grade; drives scarcity pricing."],
      ["Graded premium", "Graded sold price divided by raw market value for the same code."],
      ["Rule of thumb", "Submission needs a clear multiple over raw plus fees to make sense."],
      ["Risk", "A near-mint raw card can return a 9 instead of a 10, which usually erases the premium."],
    ],
    pitfalls: [
      ["Treating all tens as equal", "Company standards differ; comps must match the company on the label."],
      ["Comping a base card against a parallel", "The suffix changes the market entirely, graded or not."],
      ["Ignoring fees and turnaround", "Grading cost, postage and months of waiting are real parts of the maths."],
      ["Submitting cards with edge wear", "Anything visible under a light will cap the grade below the premium tier."],
    ],
    faq: [
      ["Is grading Palworld TCG cards worth it?", "Only when the graded premium clearly exceeds raw value plus grading and shipping costs, which in practice means top-rarity cards in flawless condition."],
      ["Does population matter for Palworld card prices?", "Yes. A low certified population at the top grade is what sustains a large premium."],
      ["Can I compare a PSA 10 to a BGS 10?", "Not directly — treat each company's grade as its own market and comp within it."],
      ["Where do graded Palworld comps come from?", "Sold graded listings for the same code and grade, which the price hub surfaces beside the raw market value."],
    ],
  },
  {
    slug: "palworld-card-liquidity-score",
    title: "Palworld Card Liquidity Scores",
    desc: "What a Palworld card liquidity score measures, how listings, sales and spread feed it, and how to use it to decide what to buy, hold or trade away.",
    keywords:
      "palworld card liquidity, palworld tcg market depth, how fast do palworld cards sell, palworld card spread, palworld tcg investing risk",
    hub: "/tcg-price-index",
    hubLabel: "Palworld TCG price index",
    tag: "TCG Market",
    question: "What does a Palworld card liquidity score mean?",
    answer:
      "Liquidity is a 0-100 score for how easily a Palworld card converts to cash at its quoted price. It rises with the number of live listings, recent sold activity and marketplace coverage, and falls as the gap between asking and sold prices widens. A high score means the quote is trustworthy; a low score means the quote is a guess.",
    steps: [
      ["Read liquidity beside the price, never instead of it", "A high price on a score of 10 is a thin, fragile number."],
      ["Prefer high-liquidity cards for value storage", "They can be sold in days without discounting."],
      ["Treat low-liquidity chase cards as long holds", "Thin markets need patience and a wide margin."],
      ["Use it to sequence a want list", "Chase the liquid holes first; they close fastest."],
      ["Re-check after a print run or reprint news", "New supply moves liquidity before it moves price."],
    ],
    data: [
      ["Scale", "0-100, higher is more liquid."],
      ["Inputs", "Live listing count, recent sold activity, marketplace coverage and ask-to-sold spread."],
      ["High score", "Quote is well-evidenced; expect to transact near it."],
      ["Low score", "Few data points; treat the quote as indicative only."],
      ["Where to see it", "A per-card column on the price index, next to market value and trend."],
    ],
    pitfalls: [
      ["Buying an illiquid card at a liquid card's multiple", "Thin markets fall hardest when sentiment cools."],
      ["Assuming rarity equals value", "A rare card nobody trades has no reliable price at all."],
      ["Reading a single sale as a market", "One outlier sale on a thin card is noise, not a level."],
      ["Ignoring the spread", "Spread is the fastest early warning that a market is drying up."],
    ],
    faq: [
      ["How is Palworld card liquidity calculated?", "From live listing counts, recent sold activity, how many marketplaces quote the card, and the ask-to-sold spread, combined into a 0-100 score."],
      ["Which Palworld cards are most liquid?", "Base-rarity singles from the main set and popular playables — they list and sell constantly."],
      ["Should I invest in low-liquidity Palworld cards?", "Only with a long horizon and a wide margin, because exiting at the quoted price can take months."],
      ["Does liquidity predict price direction?", "No, but it tells you how much confidence the price itself deserves."],
    ],
  },
  {
    slug: "palworld-tcgplayer-prices",
    title: "Palworld TCGplayer Prices",
    desc: "How Palworld TCGplayer prices compare with eBay sold data, why the two disagree, and how to use both feeds together to price a card correctly.",
    keywords:
      "palworld tcgplayer prices, palworld tcg market price, palworld card price comparison, tcgplayer vs ebay palworld cards, palworld tcg price feeds",
    hub: "/prices",
    hubLabel: "Palworld TCG price hub",
    tag: "TCG Market",
    question: "How do Palworld TCGplayer prices compare to eBay?",
    answer:
      "TCGplayer quotes are seller-set market prices from a dedicated card marketplace, while eBay data mixes auctions, fixed prices and sold results from a general marketplace. On the same Palworld card the two usually sit within a normal band of each other, and a large gap is a signal — thin supply on one side, or a mislabelled printing.",
    steps: [
      ["Match the printed code across both feeds", "Set and suffix must be identical before any comparison is meaningful."],
      ["Compare sold to sold where possible", "A seller-set market price and a live auction ask are different kinds of number."],
      ["Note the direction of the gap", "Consistently cheaper on one marketplace usually means deeper supply there."],
      ["Treat a very large gap as a data problem first", "Check for a mislabelled parallel or a condition difference before assuming arbitrage."],
      ["Use the combined view to price your card", "Two independent feeds agreeing is far stronger evidence than either alone."],
    ],
    data: [
      ["TCGplayer", "Dedicated card marketplace; seller-set market prices per printing."],
      ["eBay", "General marketplace; auctions, fixed prices and sold results."],
      ["Best use", "Cross-check — agreement between feeds raises confidence in the quote."],
      ["Common gap cause", "Different supply depth per marketplace for the same code."],
      ["Red flag", "A multiple-times gap normally means the printings are not actually the same."],
    ],
    pitfalls: [
      ["Comparing a market price to an asking price", "They answer different questions; keep sold against sold."],
      ["Assuming a gap is free money", "Fees, shipping and time usually consume a modest spread."],
      ["Ignoring condition fields", "Near-mint and lightly played quotes are separate markets."],
      ["Using one feed for a thin card", "Low-liquidity cards need every data point you can gather."],
    ],
    faq: [
      ["Which is more accurate for Palworld cards, TCGplayer or eBay?", "Neither alone. TCGplayer is cleaner per printing, eBay has more sold volume; the two together give the most reliable level."],
      ["Why is a Palworld card cheaper on one marketplace?", "Supply depth differs by marketplace, and thin supply on one side lifts its price."],
      ["Do Palworld cards have official prices?", "No. Every quote is a market observation, which is why cross-checking feeds matters."],
      ["How often do the price feeds update?", "The price hub refreshes its market feeds daily, and live listings are pulled on demand when you open a card."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Work through this with the ${t.hubLabel} open at ${t.hub}, so every number you act on is the live one.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What the market actually rewards", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${t.hubLabel}`, text: `${hubSentence} Apply the filters described below and work from the tool's output rather than a remembered price.` },
      ],
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The exact order to follow. Each step removes a mistake that costs real money.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `The values and definitions behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four failure modes that account for most wasted money on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions collectors ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(4),
    },
  ];
}

export const TCG_FEATURE_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-09-02",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each new market cluster supports, for internal-link rendering. */
export const TCG_FEATURE_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
